import { useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import { useLocation } from 'react-router-dom';
import Fa from '../../../utils/Fa';
import Toggle from '../../../utils/Toggle';

import './NavCollapsible.css';

interface NavCollapsibleProps {
  children: React.ReactNode;
}

export default function NavCollapsible({ children }: NavCollapsibleProps): JSX.Element {
  const [collapsed, setCollapsed] = useState(true);
  const el = useRef<HTMLDivElement>(null);
  const location = useLocation();

  function toggleCollapse(): void {
    setCollapsed(!collapsed);
  }

  function handleFocusIn(e: MouseEvent): void {
    if (!e.target || !(e.target instanceof HTMLElement)) {
      return;
    }
    const containsFocusedEl = el.current && el.current.contains(e.target);
    if (!containsFocusedEl && !collapsed) {
      toggleCollapse();
    }
  }

  useEffect(() => {
    setCollapsed(true);
  }, [location]);

  useEffect(() => {
    document.addEventListener('click', handleFocusIn);
    return () => {
      document.removeEventListener('click', handleFocusIn);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [collapsed]);

  function handleClick(): void {
    toggleCollapse();
  }

  return (
    <div className="nav-collapsible">
      <Toggle show="xs sm">
        <button
          type="button"
          className={classNames('nav-collapsible-button', { collapsed })}
          onClick={handleClick}
          aria-label="Open navigation"
        >
          <Fa icon={collapsed ? 'bars' : 'times'} aria-hidden="true" />
        </button>
      </Toggle>
      <div className={classNames('nav-collapsible-background', { collapsed })} />
      <div className={classNames('nav-collapsible-content', { collapsed })} ref={el}>
        {children}
      </div>
    </div>
  );
}
